import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchCharacterInventory,
  selectCharacter,
  selectLoading,
} from '../ViewInventory/inventorySlice'
import {
  fetchProgressionAchievements,
  fetchProgressionActivity,
  fetchProgressionRankPath,
} from '../../../services/progression.api'
import { previewLevelUpPVCK } from '../shared/characterDerivedStats'
import ShinobiRankTimeline from './ShinobiRankTimeline'
import './CharacterDetail.css'

const ACTIVITY_PAGE_SIZE = 20

const EVENT_LABELS = {
  xp_gain: 'Experiencia',
  ryous_gain: 'Ryous',
  reward: 'Recompensa',
  level_up: 'Subida de nivel',
  rank_up: 'Ascenso de rango',
  achievement_unlock: 'Logro',
  mission_accept: 'Misión aceptada',
  mission_complete: 'Misión completada',
  adventure_join: 'Aventura',
  adventure_leave: 'Aventura',
}

function formatRelativeDate(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const diffMs = Date.now() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  if (diffSec < 45) return 'Hace un momento'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `Hace ${diffMin} min`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `Hace ${diffHr} h`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `Hace ${diffDay} d`

  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
}

function ActivityDeltas({ event }) {
  const xp = Number(event?.deltaXp ?? 0)
  const ry = Number(event?.deltaRyous ?? 0)
  if (xp === 0 && ry === 0) return null

  return (
    <div className="char-progresion__event-deltas">
      {xp !== 0 ? (
        <span className="char-progresion__delta char-progresion__delta--xp">
          {xp > 0 ? '+' : ''}
          {xp.toLocaleString()} XP
        </span>
      ) : null}
      {ry !== 0 ? (
        <span className="char-progresion__delta char-progresion__delta--ryous">
          {ry > 0 ? '+' : ''}
          {ry.toLocaleString()} ryous
        </span>
      ) : null}
    </div>
  )
}

function ActivityContextLink({ event }) {
  const slug = event?.metadata?.adventureSlug
  if (!slug || !['adventure_join', 'adventure_leave'].includes(event?.eventType)) return null

  return (
    <Link to={`/aventura/${slug}`} className="char-progresion__feed-link">
      Ver aventura
    </Link>
  )
}

export default function CharacterProgresion() {
  const { slug } = useParams()
  const dispatch = useDispatch()
  const character = useSelector(selectCharacter)
  const loading = useSelector(selectLoading)

  const [rankPath, setRankPath] = useState(null)
  const [activity, setActivity] = useState({ items: [], meta: { total: 0, hasMore: false } })
  const [achievements, setAchievements] = useState({ achievements: [], meta: { total: 0, unlocked: 0 } })
  const [activityOffset, setActivityOffset] = useState(0)
  const [loadingRankPath, setLoadingRankPath] = useState(false)
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingAchievements, setLoadingAchievements] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (slug) {
      dispatch(fetchCharacterInventory(slug))
    }
  }, [slug, dispatch])

  const loadProgressionData = useCallback(async () => {
    if (!slug) return
    setLoadError('')
    setLoadingRankPath(true)
    setLoadingActivity(true)
    setLoadingAchievements(true)
    setActivityOffset(0)

    try {
      const [rankPathRes, activityRes, achievementsRes] = await Promise.all([
        fetchProgressionRankPath(slug).catch(() => null),
        fetchProgressionActivity(slug, { limit: ACTIVITY_PAGE_SIZE, offset: 0 }),
        fetchProgressionAchievements(slug),
      ])

      setRankPath(rankPathRes)
      setActivity({
        items: Array.isArray(activityRes?.items) ? activityRes.items : [],
        meta: activityRes?.meta || { total: 0, hasMore: false },
      })
      setAchievements({
        achievements: Array.isArray(achievementsRes?.achievements) ? achievementsRes.achievements : [],
        meta: achievementsRes?.meta || { total: 0, unlocked: 0 },
      })
    } catch (err) {
      setLoadError('No se pudo cargar la progresión del personaje.')
    } finally {
      setLoadingRankPath(false)
      setLoadingActivity(false)
      setLoadingAchievements(false)
    }
  }, [slug])

  useEffect(() => {
    loadProgressionData()
  }, [loadProgressionData])

  const handleLoadMoreActivity = async () => {
    if (!slug || loadingMore || !activity.meta?.hasMore) return
    const nextOffset = activityOffset + ACTIVITY_PAGE_SIZE
    setLoadingMore(true)
    try {
      const res = await fetchProgressionActivity(slug, {
        limit: ACTIVITY_PAGE_SIZE,
        offset: nextOffset,
      })
      setActivity((prev) => ({
        items: [...prev.items, ...(Array.isArray(res?.items) ? res.items : [])],
        meta: res?.meta || prev.meta,
      }))
      setActivityOffset(nextOffset)
    } catch (_err) {
      setLoadError('No se pudo cargar más actividad.')
    } finally {
      setLoadingMore(false)
    }
  }

  const char = character || {}
  const level = Number(char?.level || 1)
  const expCurrent = Number(char?.experience?.current || 0)
  const expMax = Number(char?.experience?.max || 0)
  const expPct = expMax > 0 ? Math.min(100, Math.round((expCurrent / expMax) * 100)) : 0
  const ryous = Number(char?.ryous ?? 0)
  const rankName = String(char?.rank || 'Genin').trim() || 'Genin'

  const levelPreview = useMemo(() => previewLevelUpPVCK(char, level), [char, level])

  if (character?.academyMode) {
    return <Navigate to={`/personajes/${slug}/ficha`} replace />
  }

  if (loading && !char?.name) {
    return (
      <div className="char-progresion">
        <div className="char-ficha__loading">
          <span className="char-ficha__loading-spinner" aria-hidden="true" />
          <p>Cargando progresión del shinobi…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="char-progresion">
      <header className="char-progresion__masthead">
        <div className="char-progresion__masthead-accent" aria-hidden="true" />
        <div className="char-progresion__masthead-body">
          <div className="char-progresion__masthead-row">
            <div className="char-progresion__masthead-intro">
              <span className="char-progresion__masthead-mark" aria-hidden="true">進</span>
              <div className="char-progresion__masthead-text">
                <p className="char-progresion__masthead-eyebrow">Camino del shinobi</p>
                <h2 className="char-progresion__title">Progresión</h2>
                <p className="char-progresion__subtitle">
                  {char?.name ? (
                    <>
                      Evolución de <span className="char-progresion__name">{char.name}</span>
                    </>
                  ) : (
                    'Nivel, rango, actividad y logros'
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="char-progresion__masthead-rule" aria-hidden="true">
            <span className="char-progresion__masthead-rule-line" />
            <span className="char-progresion__masthead-rule-gem">◆</span>
            <span className="char-progresion__masthead-rule-line" />
          </div>
        </div>
      </header>

      {loadError ? (
        <p className="char-progresion__error" role="alert">
          {loadError}{' '}
          <button type="button" className="char-progresion__retry" onClick={loadProgressionData}>
            Reintentar
          </button>
        </p>
      ) : null}

      <div className="char-progresion__grid">
        <section className="char-card char-card--section char-progresion__block char-progresion__block--timeline">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Camino del Shinobi</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          <ShinobiRankTimeline rankPath={rankPath} loading={loadingRankPath} />
        </section>

        <section className="char-card char-card--section char-progresion__block char-progresion__block--summary">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Resumen</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          <div className="char-progresion__summary">
            <div className="char-level-block char-level-block--top">
              <div className="char-level-block__header">
                <span className="char-level-block__label">Nivel</span>
                <span className="char-level-block__value">{level}</span>
              </div>
              <div className="char-expbar char-expbar--diagonal" aria-label={`Experiencia ${expCurrent} de ${expMax}`}>
                <div className="char-expbar__fill" style={{ width: `${expPct}%` }} />
              </div>
              <div className="char-level-block__xp">
                <span>{expCurrent.toLocaleString()}</span>
                <span>{expMax.toLocaleString()} XP</span>
              </div>
            </div>

            <div className="char-progresion__summary-row">
              <div className="char-progresion__stat-chip">
                <span className="char-progresion__stat-label">Rango</span>
                <span className="char-progresion__stat-value">{rankName}</span>
              </div>
              <div className="char-progresion__stat-chip">
                <span className="char-progresion__stat-label">Ryous</span>
                <span className="char-progresion__stat-value">{ryous.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="char-card char-card--section char-progresion__block char-progresion__block--mejoras">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Mejoras de nivel</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          <div className="char-progresion__derived-table" role="table" aria-label="Preview PV y CK al subir de nivel">
            <div className="char-progresion__derived-row char-progresion__derived-row--head" role="row">
              <span role="columnheader" />
              <span role="columnheader">Actual</span>
              <span role="columnheader">Nivel {level + 1}</span>
              <span role="columnheader">Δ</span>
            </div>
            <div className="char-progresion__derived-row" role="row">
              <span className="char-progresion__derived-label" role="rowheader">PV</span>
              <span role="cell">{levelPreview.current.PV}</span>
              <span role="cell">{levelPreview.next.PV}</span>
              <span className="char-progresion__derived-delta" role="cell">+{levelPreview.delta.PV}</span>
            </div>
            <div className="char-progresion__derived-row" role="row">
              <span className="char-progresion__derived-label" role="rowheader">CK</span>
              <span role="cell">{levelPreview.current.CK}</span>
              <span role="cell">{levelPreview.next.CK}</span>
              <span className="char-progresion__derived-delta" role="cell">+{levelPreview.delta.CK}</span>
            </div>
          </div>
        </section>

        <section className="char-card char-card--section char-progresion__block char-progresion__block--feed">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Historial</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          {loadingActivity ? (
            <p className="char-progresion__empty">Cargando actividad…</p>
          ) : activity.items.length === 0 ? (
            <p className="char-progresion__empty">
              Aún no hay acciones registradas. Unirse o abandonar aventuras, aceptar o completar misiones, subir de
              nivel y desbloquear logros aparecerán aquí en orden cronológico.
            </p>
          ) : (
            <>
              <ul className="char-progresion__feed">
                {activity.items.map((event) => (
                  <li
                    key={event.eventId}
                    className={`char-progresion__feed-item char-progresion__feed-item--${event.eventType || 'default'}`}
                  >
                    <div className="char-progresion__feed-main">
                      <span className="char-progresion__feed-type">
                        {EVENT_LABELS[event.eventType] || 'Actividad'}
                      </span>
                      <p className="char-progresion__feed-title">{event.title}</p>
                      {event.summary ? (
                        <p className="char-progresion__feed-summary">{event.summary}</p>
                      ) : null}
                      <ActivityDeltas event={event} />
                      <ActivityContextLink event={event} />
                    </div>
                    <time className="char-progresion__feed-time" dateTime={event.createdAt}>
                      {formatRelativeDate(event.createdAt)}
                    </time>
                  </li>
                ))}
              </ul>
              {activity.meta?.hasMore ? (
                <button
                  type="button"
                  className="char-btn char-btn--ghost char-progresion__load-more"
                  onClick={handleLoadMoreActivity}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Cargando…' : 'Cargar más'}
                </button>
              ) : null}
            </>
          )}
        </section>

        <section className="char-card char-card--section char-progresion__block char-progresion__block--achievements">
          <header className="char-section-header">
            <span className="char-section-header__ornament" aria-hidden="true" />
            <h3 className="char-section-header__title">Logros e hitos</h3>
            <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
          </header>

          {loadingAchievements ? (
            <p className="char-progresion__empty">Cargando logros…</p>
          ) : achievements.achievements.length === 0 ? (
            <p className="char-progresion__empty">No hay logros definidos todavía.</p>
          ) : (
            <div className="char-progresion__achievements">
              {achievements.achievements.map((ach) => (
                <article
                  key={ach.achievementId ?? ach.key}
                  className={`char-progresion__achievement${
                    ach.unlocked ? ' char-progresion__achievement--unlocked' : ' char-progresion__achievement--locked'
                  }`}
                >
                  <span className="char-progresion__achievement-icon" aria-hidden="true">
                    {ach.icon || '◎'}
                  </span>
                  <div className="char-progresion__achievement-body">
                    <h4 className="char-progresion__achievement-name">{ach.name}</h4>
                    <p className="char-progresion__achievement-desc">{ach.description}</p>
                    {ach.unlocked && ach.unlockedAt ? (
                      <p className="char-progresion__achievement-date">
                        Desbloqueado · {formatRelativeDate(ach.unlockedAt)}
                      </p>
                    ) : (
                      <p className="char-progresion__achievement-locked-label">Bloqueado</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="char-cta-band char-cta-band--sheet" aria-label="Acciones rápidas">
        <Link to={`/personajes/${slug}/ficha`} className="char-btn char-btn--ghost">
          Volver a la ficha
        </Link>
      </div>
    </div>
  )
}
